import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Chip,
  Typography,
  TextField,
  Button,
} from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { ArrowUpward, ArrowDownward } from '@mui/icons-material'
import { StructuredQuestion } from '../interfaces/questions'

interface DraggableQuestionCardProps {
  question: StructuredQuestion
  index: number
  onReorder: (fromIndex: number, toIndex: number) => void
  onUpdateQuestionNumber?: (newNumber: string) => void
  onInsertAbove?: () => void
  onInsertBelow?: () => void
}

export default function DraggableQuestionCard({
  question,
  index,
  onReorder,
  onUpdateQuestionNumber,
  onInsertAbove,
  onInsertBelow,
}: DraggableQuestionCardProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [editingNumber, setEditingNumber] = useState<string | null>(null)

  // Reset local edit state when the stored value changes (e.g. after a drag reorder)
  useEffect(() => {
    setEditingNumber(null)
  }, [question.question_number])

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', index.toString())
    setIsDragging(true)
  }

  const handleDragEnd = () => {
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'))
    const toIndex = index

    if (fromIndex !== toIndex) {
      onReorder(fromIndex, toIndex)
    }
  }

  const commitNumberEdit = () => {
    if (
      editingNumber !== null &&
      editingNumber.trim() &&
      editingNumber !== question.question_number
    ) {
      onUpdateQuestionNumber?.(editingNumber.trim())
    }
    setEditingNumber(null)
  }

  return (
    <Paper
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        p: 2,
        mb: 2,
        bgcolor: isDragOver ? '#e3f2fd' : '#fafafa',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
        border: isDragOver ? '2px dashed #1976d2' : '1px solid #e0e0e0',
        transition: 'all 0.2s ease',
        '&:active': {
          cursor: 'grabbing',
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ pt: 0.5 }}>
          <DragIndicatorIcon color="action" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {onUpdateQuestionNumber ? (
                <TextField
                  size="small"
                  value={editingNumber ?? question.question_number}
                  onChange={(e) => setEditingNumber(e.target.value)}
                  onBlur={commitNumberEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter')
                      (e.target as HTMLInputElement).blur()
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{ width: 80 }}
                  inputProps={{ style: { fontSize: '0.75rem', padding: '4px 8px' } }}
                />
              ) : (
                <Chip
                  label={question.question_number}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              <Chip label={question.question_type} size="small" />
              {question.section && (
                <Chip
                  label={`Section: ${question.section}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {onInsertAbove && (
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  startIcon={<ArrowUpward />}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={onInsertAbove}
                  sx={{ textTransform: 'none' }}
                >
                  Add Above
                </Button>
              )}
              {onInsertBelow && (
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  startIcon={<ArrowDownward />}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={onInsertBelow}
                  sx={{ textTransform: 'none' }}
                >
                  Add Below
                </Button>
              )}
            </Box>
          </Box>
          <Typography variant="body1" fontWeight="500" sx={{ mb: 1 }}>
            {question.question}
          </Typography>
          {question.question_details && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {question.question_details}
            </Typography>
          )}
          {question.options && question.options.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" fontWeight="500">
                Options:
              </Typography>
              <Box sx={{ ml: 2 }}>
                {question.options.map((opt, idx) => (
                  <Typography key={idx} variant="body2">
                    • {opt}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {question.required && (
              <Chip label="Required" size="small" color="error" />
            )}
            {!question.required && (
              <Chip label="Optional" size="small" variant="outlined" />
            )}
            {question.attachment_required && (
              <Chip label="Attachment Required" size="small" color="warning" />
            )}
            {question.char_limit && (
              <Chip
                label={`Max ${question.char_limit} chars`}
                size="small"
                variant="outlined"
              />
            )}
            {question.word_limit && (
              <Chip
                label={`Max ${question.word_limit} words`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  )
}
